/**
 * Bonus games (live `/api/bonus-games`, settled `/api/bonus-games-final`), player progress data
 * (`/api/progress-data`) and progressive jackpots (`/api/progressive-jackpot`, `…-draws`). Read-only
 * views. Traps (rgs-api-contract api-traps):
 * - #11 `POST /bonus-games` is test scaffolding: never exposed.
 * - #12 `/progress-data/:id` takes a player id but the key is (player, game): rows are read from the
 *   list only.
 * - #14 draws default to a sort field that doesn't exist (400): a sort is always sent.
 * - #3 `/progress-data/:id` nests `Game.EngineApiKey`: nested objects keep only the fields shown here.
 * - Paged lists turn raw Date columns into `{}`: list dates parse as missing.
 */
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { http } from '@/api/http';
import {
  encodePagedQuery,
  pagedSchema,
  type FilterDescriptor,
  type PagedQuery,
  type SortDescriptor,
} from '@/api/paged-query';
import { bigintString, jsonColumn } from '@/api/zod-helpers';
import { filterParam, listSearchSchema, toPagedQuery } from '@/lib/url-state';

const looseDate = z.unknown().transform((v) => (typeof v === 'string' && v.trim() !== '' ? v : null));
const json = jsonColumn(z.unknown());
const optionalNumber = z.coerce.number().nullable().catch(null);
const optionalString = z.string().nullable().optional().catch(null);

export const bonusKeys = {
  all: ['bonus'] as const,
  games: (kind: BonusKind, q: PagedQuery) => ['bonus', kind, 'list', q] as const,
  game: (kind: BonusKind, id: string) => ['bonus', kind, 'detail', id] as const,
  progress: (q: PagedQuery) => ['bonus', 'progress', q] as const,
  jackpots: (q: PagedQuery) => ['bonus', 'jackpots', q] as const,
  draws: (q: PagedQuery) => ['bonus', 'draws', q] as const,
  gameNames: ['bonus', 'game-names'] as const,
  brandNames: ['bonus', 'brand-names'] as const,
};

// ---- bonus games ----------------------------------------------------------------------------

export type BonusKind = 'live' | 'settled';
const BONUS_PATH: Record<BonusKind, string> = { live: '/bonus-games', settled: '/bonus-games-final' };

export const bonusGameSchema = z.object({
  Id: bigintString,
  roundId: bigintString.nullable().catch(null),
  playerId: optionalNumber,
  gameId: optionalNumber,
  /** Cents in the player's currency (like round amounts); the currency isn't part of the row. */
  WinAmount: z.string().nullable().catch(null),
  BonusGameName: z.string().nullable().catch(null),
  TriggeredBy: z.string().nullable().catch(null),
  TriggeredByStack: z.string().nullable().catch(null),
  TriggeringGameState: json,
  RelatedBonusGameId: optionalNumber,
  GameState: json,
  BetState: json,
  Status: optionalNumber,
  CreatedAt: looseDate,
  UpdatedAt: looseDate,
  Game: z.object({ Name: z.string() }).partial().nullable().optional().catch(null),
  Player: z
    .object({ Id: z.coerce.number(), OperatorPlayerNickname: optionalString, Currency: optionalString })
    .partial()
    .nullable()
    .optional()
    .catch(null),
});
export type BonusGame = z.infer<typeof bonusGameSchema>;

/** The server's allow-list (module-fields.config.ts `bonus-games`, used by both tables). */
export const BONUS_SORT_FIELDS = [
  'Id',
  'roundId',
  'WinAmount',
  'BonusGameName',
  'TriggeredBy',
  'TriggeredByStack',
  'Status',
] as const;
export const DEFAULT_BONUS_SORT: SortDescriptor = { field: 'Id', dir: 'desc' };

export const bonusSearchSchema = listSearchSchema({
  sortFields: BONUS_SORT_FIELDS,
  filters: { round: filterParam.string(), status: filterParam.number(), trigger: filterParam.string() },
});
export type BonusSearch = z.infer<typeof bonusSearchSchema>;

export function bonusPagedQuery(search: BonusSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'BonusGameName', operator: 'contains', value: search.q });
  if (search.round) filters.push({ field: 'roundId', operator: 'eq', value: search.round });
  if (search.status !== undefined) filters.push({ field: 'Status', operator: 'eq', value: search.status });
  if (search.trigger) filters.push({ field: 'TriggeredBy', operator: 'eq', value: search.trigger });
  return toPagedQuery(search, { defaultSort: DEFAULT_BONUS_SORT, filters });
}

const bonusListSchema = pagedSchema(bonusGameSchema);

export const bonusGamesQuery = (kind: BonusKind, search: BonusSearch) => {
  const query = bonusPagedQuery(search);
  return queryOptions({
    queryKey: bonusKeys.games(kind, query),
    queryFn: ({ signal }) => http(BONUS_PATH[kind], { query: encodePagedQuery(query), signal }, bonusListSchema),
    placeholderData: keepPreviousData,
  });
};

export const bonusGameQuery = (kind: BonusKind, id: string) =>
  queryOptions({
    queryKey: bonusKeys.game(kind, id),
    queryFn: ({ signal }) => http(`${BONUS_PATH[kind]}/${encodeURIComponent(id)}`, { signal }, bonusGameSchema),
  });

export const BONUS_STATUSES: Record<number, { label: string; tone: 'info' | 'success' }> = {
  0: { label: 'In progress', tone: 'info' },
  1: { label: 'Finished', tone: 'success' },
};

/** Original games record multi-step mechanics here; `$`-names are steps, not bonus features. */
export const isStepMechanic = (name: string | null) => !!name && name.startsWith('$');

// ---- progress data --------------------------------------------------------------------------

export const progressSchema = z.object({
  playerId: z.coerce.number(),
  gameId: z.coerce.number(),
  Data: json,
  UpdatedAt: looseDate,
  CreatedAt: looseDate,
  Player: z
    .object({ Id: z.coerce.number(), OperatorPlayerNickname: optionalString })
    .partial()
    .nullable()
    .optional()
    .catch(null),
  Game: z.object({ Name: z.string() }).partial().nullable().optional().catch(null),
});
export type ProgressRow = z.infer<typeof progressSchema>;

/** The allow-list also names `Player.OperatorPlayerNickname` and `Game.Name`: sorting on them is a 400. */
export const PROGRESS_SORT_FIELDS = ['playerId', 'CreatedAt'] as const;
export const DEFAULT_PROGRESS_SORT: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

export const progressSearchSchema = listSearchSchema({
  sortFields: PROGRESS_SORT_FIELDS,
  filters: { player: filterParam.number(), game: filterParam.string() },
});
export type ProgressSearch = z.infer<typeof progressSearchSchema>;

export function progressPagedQuery(search: ProgressSearch): PagedQuery {
  const filters: FilterDescriptor[] = [];
  if (search.q) filters.push({ field: 'Player.OperatorPlayerNickname', operator: 'contains', value: search.q });
  if (search.player !== undefined) filters.push({ field: 'playerId', operator: 'eq', value: search.player });
  if (search.game) filters.push({ field: 'Game.Name', operator: 'contains', value: search.game });
  return toPagedQuery(search, { defaultSort: DEFAULT_PROGRESS_SORT, filters });
}

export const progressQuery = (search: ProgressSearch) => {
  const query = progressPagedQuery(search);
  return queryOptions({
    queryKey: bonusKeys.progress(query),
    queryFn: ({ signal }) =>
      http('/progress-data', { query: encodePagedQuery(query), signal }, pagedSchema(progressSchema)),
    placeholderData: keepPreviousData,
  });
};

// ---- progressive jackpots -------------------------------------------------------------------

export const TIERS = ['Tier1', 'Tier2', 'Tier3', 'Tier4'] as const;

export const jackpotSchema = z.object({
  BrandId: z.coerce.number(),
  Tier1: z.string().nullable().catch(null),
  Tier2: z.string().nullable().catch(null),
  Tier3: z.string().nullable().catch(null),
  Tier4: z.string().nullable().catch(null),
  Tier1Hidden: z.string().nullable().catch(null),
  Tier2Hidden: z.string().nullable().catch(null),
  Tier3Hidden: z.string().nullable().catch(null),
  Tier4Hidden: z.string().nullable().catch(null),
});
export type Jackpot = z.infer<typeof jackpotSchema>;

export const JACKPOT_SORT_FIELDS = [
  'BrandId',
  ...TIERS,
  'Tier1Hidden',
  'Tier2Hidden',
  'Tier3Hidden',
  'Tier4Hidden',
] as const;
export const DEFAULT_JACKPOT_SORT: SortDescriptor = { field: 'BrandId', dir: 'asc' };

export const jackpotSearchSchema = listSearchSchema({ sortFields: JACKPOT_SORT_FIELDS });
export type JackpotSearch = z.infer<typeof jackpotSearchSchema>;

export const jackpotsQuery = (search: JackpotSearch) => {
  const query = toPagedQuery(search, { defaultSort: DEFAULT_JACKPOT_SORT });
  return queryOptions({
    queryKey: bonusKeys.jackpots(query),
    queryFn: ({ signal }) =>
      http('/progressive-jackpot', { query: encodePagedQuery(query), signal }, pagedSchema(jackpotSchema)),
    placeholderData: keepPreviousData,
  });
};

export const drawSchema = z.object({
  Id: z.coerce.number(),
  TierName: z.string().nullable().catch(null),
  RoundId: bigintString.nullable().catch(null),
  PlayerId: optionalNumber,
  OperatorId: optionalNumber,
  BrandId: optionalNumber,
  AmountInBaseCurrency: z.string().nullable().catch(null),
  AmountInPlayerCurrency: z.string().nullable().catch(null),
  Status: optionalNumber,
  CreatedAt: looseDate,
  UpdatedAt: looseDate,
  OperatorCreditResponse: json,
});
export type Draw = z.infer<typeof drawSchema>;

/** The allow-list also names `JackpotId`, which isn't a column: sorting on it is a 400 (trap #14). */
export const DRAW_SORT_FIELDS = ['Status', 'CreatedAt'] as const;
/** Always sent: the server's default sort field doesn't exist (trap #14). */
export const DEFAULT_DRAW_SORT: SortDescriptor = { field: 'CreatedAt', dir: 'desc' };

export const drawSearchSchema = listSearchSchema({
  sortFields: DRAW_SORT_FIELDS,
  filters: { status: filterParam.number() },
});
export type DrawSearch = z.infer<typeof drawSearchSchema>;

export const drawsQuery = (search: DrawSearch) => {
  const filters: FilterDescriptor[] =
    search.status === undefined ? [] : [{ field: 'Status', operator: 'eq', value: search.status }];
  const query = toPagedQuery(search, { defaultSort: DEFAULT_DRAW_SORT, filters });
  return queryOptions({
    queryKey: bonusKeys.draws(query),
    queryFn: ({ signal }) =>
      http('/progressive-jackpot-draws', { query: encodePagedQuery(query), signal }, pagedSchema(drawSchema)),
    placeholderData: keepPreviousData,
  });
};

// ---- name lookups (read-only; features never import each other) -----------------------------

/** Game names by id. Only Id/Name are kept (game rows also carry `EngineApiKey`). */
export const gameNamesQuery = () =>
  queryOptions({
    queryKey: bonusKeys.gameNames,
    queryFn: ({ signal }) =>
      http(
        '/games',
        { query: encodePagedQuery({ skip: 0, take: 1000, sort: { field: 'Name', dir: 'asc' } }), signal },
        pagedSchema(z.object({ Id: z.coerce.number(), Name: z.string() })),
      ).then((page) => new Map(page.data.map((g) => [g.Id, g.Name]))),
    staleTime: 5 * 60_000,
  });

/** Brand names by numeric id (jackpot tiers are keyed by it). */
export const brandNamesQuery = () =>
  queryOptions({
    queryKey: bonusKeys.brandNames,
    queryFn: ({ signal }) =>
      http(
        '/brands',
        { query: encodePagedQuery({ skip: 0, take: 1000 }), signal },
        pagedSchema(z.object({ Id: z.coerce.number(), Name: z.string().nullable().catch(null) })),
      ).then((page) => new Map(page.data.map((b) => [b.Id, b.Name ?? `Brand ${b.Id}`]))),
    staleTime: 5 * 60_000,
  });
